import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { supabase } from '@/lib/supabase';
import CampoTexto from '@/components/base/CampoTexto';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import CampoImagem from './CampoImagem';
import EspelhoRifa from './EspelhoRifa';
import { combinarDataHora, mascaraCNPJ, mensagemErro } from '../utils';
import { formatarValor } from '@/pages/evento/utils';
import type { ComissaoInfo, FormaParticipar, ImagemDraft, SorteioInfo } from '../types';

export interface PassoRifaHandle {
  validar: () => Promise<boolean>;
}

interface PassoRifaProps {
  eventoId: number | null;
  nomeEvento: string;
  participacoes: FormaParticipar[];
}

type SorteioPor = 'instagram' | 'facebook' | 'loteria_federal';

const OPCOES_SORTEIO: { valor: SorteioPor; rotulo: string }[] = [
  { valor: 'loteria_federal', rotulo: 'Loteria Federal' },
  { valor: 'instagram', rotulo: 'Instagram' },
  { valor: 'facebook', rotulo: 'Facebook' },
];

const inputCls =
  'mt-1 w-full cursor-pointer rounded-md border border-secondary-300 bg-white px-3 py-2 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500';

function imagemVaziaRifa(): ImagemDraft {
  return { arquivo: null, url: null, removida: false, carregando: false };
}

function primeiroValorCentavos(participacoes: FormaParticipar[]): number {
  for (const p of participacoes) {
    if (p.valorModo !== 'pago') continue;
    if (p.dividirGrupos) {
      const g = p.grupos.find((gr) => gr.valorCentavos > 0);
      if (g) return g.valorCentavos;
    } else if (p.valorCentavos > 0) {
      return p.valorCentavos;
    }
  }
  return 0;
}

function capacidadeTotal(participacoes: FormaParticipar[]): number {
  return participacoes.reduce((acc, p) => acc + (p.temLimite && p.vagas ? p.vagas : 0), 0);
}

function PassoRifa(
  { eventoId, nomeEvento, participacoes }: PassoRifaProps,
  ref: React.Ref<PassoRifaHandle>,
) {
  // Decisão espelho × formulário. Tudo vem de `como_recebe_o_evento`.
  const [carregando, setCarregando] = useState(true);
  const [sorteio, setSorteio] = useState<SorteioInfo | null>(null);
  const [comissao, setComissao] = useState<ComissaoInfo | null>(null);
  const [falhaConferencia, setFalhaConferencia] = useState(false);

  // Formulário de criação (só aparece quando `sorteio` é null).
  const [premioPrincipal, setPremioPrincipal] = useState('');
  const [fotoPrincipal, setFotoPrincipal] = useState<ImagemDraft>(imagemVaziaRifa());
  const [premioSegundo, setPremioSegundo] = useState('');
  const [premioTerceiro, setPremioTerceiro] = useState('');

  const [valorCentavos, setValorCentavos] = useState(0);
  const [porTalao, setPorTalao] = useState('20');
  const [quantidadeTotal, setQuantidadeTotal] = useState('');

  const [sorteioData, setSorteioData] = useState('');
  const [sorteioHora, setSorteioHora] = useState('');
  const [sorteioPor, setSorteioPor] = useState<SorteioPor>('loteria_federal');
  const [sorteioOnde, setSorteioOnde] = useState('');

  const [entidadeNome, setEntidadeNome] = useState('');
  const [entidadeCnpj, setEntidadeCnpj] = useState('');
  const [autorizacaoSpa, setAutorizacaoSpa] = useState('');

  const [erros, setErros] = useState<Record<string, string>>({});
  const [salvando, setSalvando] = useState(false);

  const porTalaoNum = Number(porTalao) || 0;
  const talaoCentavos = valorCentavos * porTalaoNum;
  const abateCentavos = Math.round(talaoCentavos * 0.93);
  const inscricaoCentavos = primeiroValorCentavos(participacoes);
  const capacidade = capacidadeTotal(participacoes);
  const sugestaoTotal = Math.ceil(capacidade * porTalaoNum * 1.15);

  const recarregar = useCallback(async (): Promise<void> => {
    if (!eventoId) return;
    const { data, error } = await supabase.rpc('como_recebe_o_evento', { p_evento: eventoId });
    if (error) throw error;
    const d = data as { sorteio?: SorteioInfo | null; comissao?: ComissaoInfo | null } | null;
    setSorteio(d?.sorteio ?? null);
    setComissao(d?.comissao ?? null);
  }, [eventoId]);

  useEffect(() => {
    if (!eventoId) {
      setCarregando(false);
      return;
    }
    let ativo = true;
    setCarregando(true);
    recarregar()
      .then(() => {
        if (ativo) setFalhaConferencia(false);
      })
      .catch(() => {
        // Não conseguiu conferir (função fora do ar, por exemplo): mantém o
        // formulário de criar, mas avisa que não dá para garantir que não existe.
        if (ativo) {
          setSorteio(null);
          setFalhaConferencia(true);
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [recarregar]);

  const aoPublicar = useCallback(() => {
    recarregar().catch(() => {});
  }, [recarregar]);

  useImperativeHandle(ref, () => ({
    validar: async () => {
      // Se já existe uma ação entre amigos, nada a criar — o passo só mostra o espelho.
      if (sorteio) return true;
      return salvar();
    },
  }));

  async function subirFoto(imagem: ImagemDraft): Promise<string | null> {
    if (!imagem.arquivo) return null;
    const nome = imagem.arquivo.name || 'premio.jpg';
    const ext = nome.split('.').pop()?.toLowerCase() || 'jpg';
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const uid = session?.user?.id || 'anonimo';
    const caminho = `${uid}/premio-${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('capas').upload(caminho, imagem.arquivo);
    if (error) return null;
    const { data } = supabase.storage.from('capas').getPublicUrl(caminho);
    return data.publicUrl;
  }

  async function salvar(): Promise<boolean> {
    const novos: Record<string, string> = {};
    if (!premioPrincipal.trim()) novos.premioPrincipal = 'Diga qual é o prêmio principal.';
    if (valorCentavos <= 0) novos.valor = 'Informe o valor de cada número.';
    if (porTalaoNum <= 0) novos.porTalao = 'Informe quantos números por talão.';
    const totalNum = Number(quantidadeTotal) || sugestaoTotal;
    if (totalNum <= 0) novos.total = 'Informe quantos números no total.';
    if (!sorteioData) novos.sorteioData = 'Escolha a data da ação entre amigos.';
    if (!sorteioHora) novos.sorteioHora = 'Escolha o horário da ação entre amigos.';
    if ((sorteioPor === 'instagram' || sorteioPor === 'facebook') && !sorteioOnde.trim()) {
      novos.sorteioOnde = 'Informe o @ do perfil.';
    }
    setErros(novos);
    if (Object.keys(novos).length > 0) return false;

    if (!eventoId) {
      setErros({ geral: 'Salve o evento antes de montar a ação entre amigos.' });
      return false;
    }

    setSalvando(true);
    setErros({});
    try {
      const imagemUrl = await subirFoto(fotoPrincipal);

      const premios: { descricao: string; imagem_url: string | null; doado_por: null }[] = [
        { descricao: premioPrincipal.trim(), imagem_url: imagemUrl, doado_por: null },
      ];
      if (premioSegundo.trim()) {
        premios.push({ descricao: premioSegundo.trim(), imagem_url: null, doado_por: null });
      }
      if (premioTerceiro.trim()) {
        premios.push({ descricao: premioTerceiro.trim(), imagem_url: null, doado_por: null });
      }

      const { data, error } = await supabase.rpc('criar_rifa_completa', {
        p: {
          modo: 'evento',
          evento_beneficiado_id: eventoId,
          premios,
          sorteio_em: combinarDataHora(sorteioData, sorteioHora),
          sorteio_por: sorteioPor,
          sorteio_onde: sorteioOnde.trim() || null,
          valor_cota_reais: valorCentavos / 100,
          numeros_por_talao: porTalaoNum,
          quantidade_cotas: totalNum,
          entidade_nome: entidadeNome.trim() || null,
          entidade_cnpj: entidadeCnpj.trim() || null,
          autorizacao_spa: autorizacaoSpa.trim() || null,
        },
      });
      if (error) throw error;
      const resultado = data as { rifa_id?: number } | null;
      if (!resultado?.rifa_id) {
        setErros({ geral: 'A ação entre amigos foi criada, mas não conseguimos o identificador. Tente de novo.' });
        return false;
      }
      await recarregar();
      return true;
    } catch (e) {
      setErros({ geral: mensagemErro(e, 'Não foi possível criar a ação entre amigos. Tente novamente.') });
      return false;
    } finally {
      setSalvando(false);
    }
  }

  const mostraConta = talaoCentavos > 0 && inscricaoCentavos > 0;

  if (carregando) {
    return (
      <div className="space-y-8">
        <div>
          <div className="h-7 w-64 animate-pulse rounded bg-secondary-100" />
          <div className="mt-2 h-4 w-80 animate-pulse rounded bg-secondary-100" />
        </div>
        <div className="h-56 w-full animate-pulse rounded-lg bg-secondary-100" />
      </div>
    );
  }

  if (sorteio) {
    return (
      <EspelhoRifa
        sorteio={sorteio}
        comissao={comissao}
        nomeEvento={nomeEvento}
        onPublicou={aoPublicar}
      />
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">A ação entre amigos deste evento</h2>
        <p className="mt-1 text-sm text-secondary-500">
          A inscrição é paga com os números da ação entre amigos. Monte os prêmios e a ação.
        </p>
      </div>

      {falhaConferencia && (
        <p
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          data-erro="true"
        >
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
            <i className="ri-information-line text-lg" />
          </span>
          Não consegui conferir se este evento já tem uma ação entre amigos. Recarregue a página
          antes de criar uma nova.
        </p>
      )}

      {erros.geral && (
        <p
          className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700"
          role="alert"
          data-erro="true"
        >
          {erros.geral}
        </p>
      )}

      {/* Prêmios */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Os prêmios</h3>
        <div className="mt-4 space-y-5">
          <CampoTexto
            id="premio-principal"
            name="premio_principal"
            label="Prêmio principal"
            help="O que as pessoas concorrem ao comprar os números."
            value={premioPrincipal}
            onChange={(v) => {
              setPremioPrincipal(v);
              if (erros.premioPrincipal) setErros((e) => ({ ...e, premioPrincipal: '' }));
            }}
            required
            error={erros.premioPrincipal || null}
            placeholder="Uma bicicleta"
          />

          <CampoImagem
            id="foto-premio"
            label="Foto do prêmio"
            help="Opcional."
            valor={fotoPrincipal}
            onChange={setFotoPrincipal}
            proporcao="paisagem"
          />

          <CampoTexto
            id="premio-segundo"
            name="premio_segundo"
            label="2º prêmio"
            help="Opcional."
            value={premioSegundo}
            onChange={setPremioSegundo}
            placeholder="Uma cesta básica"
          />

          <CampoTexto
            id="premio-terceiro"
            name="premio_terceiro"
            label="3º prêmio"
            help="Opcional."
            value={premioTerceiro}
            onChange={setPremioTerceiro}
            placeholder="Um vale-presente"
          />
        </div>
      </section>

      {/* Números */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Os números</h3>
        <div className="mt-4 space-y-5">
          <div className="max-w-[320px]">
            <CampoDinheiro
              id="valor-numero-rifa"
              name="valor_numero"
              label="Valor de cada número"
              centavos={valorCentavos}
              onChange={(v) => {
                setValorCentavos(v);
                if (erros.valor) setErros((e) => ({ ...e, valor: '' }));
              }}
              required
              error={erros.valor || null}
              placeholder="20,00"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CampoTexto
              id="numeros-por-talao"
              name="numeros_por_talao"
              label="Números por talão"
              value={porTalao}
              onChange={(v) => {
                setPorTalao(v);
                if (erros.porTalao) setErros((e) => ({ ...e, porTalao: '' }));
              }}
              required
              error={erros.porTalao || null}
              inputMode="numeric"
            />
            <CampoTexto
              id="quantidade-total"
              name="quantidade_total"
              label="Quantos números no total?"
              help={
                capacidade > 0 && porTalaoNum > 0
                  ? `Sugerimos ${sugestaoTotal} (vagas × números por talão, com folga de 15%).`
                  : 'Compre folga: se faltar número, o talão elástico não funciona.'
              }
              value={quantidadeTotal}
              onChange={(v) => {
                setQuantidadeTotal(v);
                if (erros.total) setErros((e) => ({ ...e, total: '' }));
              }}
              required
              error={erros.total || null}
              inputMode="numeric"
              placeholder={sugestaoTotal > 0 ? String(sugestaoTotal) : '100'}
            />
          </div>

          {capacidade > 0 && porTalaoNum > 0 && (
            <button
              type="button"
              onClick={() => setQuantidadeTotal(String(sugestaoTotal))}
              className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              Usar a sugestão ({sugestaoTotal})
            </button>
          )}
        </div>
      </section>

      {/* Conta viva */}
      {mostraConta && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 px-4 py-3.5">
          {abateCentavos >= inscricaoCentavos ? (
            <p className="text-sm text-foreground-800">
              Cada talão de {porTalaoNum} números vale {formatarValor(talaoCentavos)}. Descontada a
              taxa da plataforma, abate {formatarValor(abateCentavos)} da inscrição. A inscrição
              custa {formatarValor(inscricaoCentavos)} — sobram{' '}
              {formatarValor(abateCentavos - inscricaoCentavos)}.
            </p>
          ) : (
            <p className="text-sm text-foreground-800">
              Um talão abate {formatarValor(abateCentavos)} de uma inscrição de{' '}
              {formatarValor(inscricaoCentavos)}. Faltam{' '}
              {formatarValor(inscricaoCentavos - abateCentavos)} — aumente o valor do número ou os
              números por talão.
            </p>
          )}
        </div>
      )}

      {/* Sorteio */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">A apuração</h3>
        <div className="mt-4 space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-base font-medium text-foreground-950">
                Data e hora <span className="text-danger-600">*</span>
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  type="date"
                  value={sorteioData}
                  onChange={(e) => {
                    setSorteioData(e.target.value);
                    if (erros.sorteioData) setErros((er) => ({ ...er, sorteioData: '' }));
                  }}
                  className={inputCls}
                />
                <input
                  type="time"
                  value={sorteioHora}
                  onChange={(e) => {
                    setSorteioHora(e.target.value);
                    if (erros.sorteioHora) setErros((er) => ({ ...er, sorteioHora: '' }));
                  }}
                  className={inputCls}
                />
              </div>
              {(erros.sorteioData || erros.sorteioHora) && (
                <p className="mt-1 text-sm text-danger-600" role="alert" data-erro="true">
                  {erros.sorteioData || erros.sorteioHora}
                </p>
              )}
            </div>

            <div>
              <label className="block text-base font-medium text-foreground-950">
                Onde vai ser a apuração
              </label>
              <select
                value={sorteioPor}
                onChange={(e) => setSorteioPor(e.target.value as SorteioPor)}
                className={`${inputCls} mt-1`}
              >
                {OPCOES_SORTEIO.map((o) => (
                  <option key={o.valor} value={o.valor}>
                    {o.rotulo}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {(sorteioPor === 'instagram' || sorteioPor === 'facebook') && (
            <CampoTexto
              id="sorteio-onde"
              name="sorteio_onde"
              label={sorteioPor === 'instagram' ? '@ do perfil' : '@ da página'}
              value={sorteioOnde}
              onChange={(v) => {
                setSorteioOnde(v);
                if (erros.sorteioOnde) setErros((e) => ({ ...e, sorteioOnde: '' }));
              }}
              required
              error={erros.sorteioOnde || null}
              placeholder="@nomeperfil"
            />
          )}
        </div>
      </section>

      {/* Para colocar no ar */}
      <section className="overflow-hidden rounded-lg border border-secondary-200 border-l-4 border-l-amber-400 bg-white">
        <div className="p-5">
          <h3 className="text-base font-medium text-foreground-950">Para colocar a ação entre amigos no ar</h3>
          <p className="mt-1 text-sm text-secondary-600">
            Para a ação entre amigos abrir para venda, a lei exige entidade, CNPJ e o número da autorização.
            Você pode deixar em branco agora e preencher depois: o evento publica, a ação entre amigos fica
            esperando.
          </p>

          <div className="mt-4 space-y-5">
            <CampoTexto
              id="entidade-nome"
              name="entidade_nome"
              label="Entidade responsável"
              value={entidadeNome}
              onChange={setEntidadeNome}
              placeholder="Paróquia São José"
            />
            <CampoTexto
              id="entidade-cnpj"
              name="entidade_cnpj"
              label="CNPJ"
              value={entidadeCnpj}
              onChange={(v) => setEntidadeCnpj(mascaraCNPJ(v))}
              inputMode="numeric"
              placeholder="00.000.000/0000-00"
            />
            <CampoTexto
              id="autorizacao-spa"
              name="autorizacao_spa"
              label="Nº da autorização (SPA/Ministério da Fazenda)"
              value={autorizacaoSpa}
              onChange={setAutorizacaoSpa}
              placeholder="0000000/0000"
            />
          </div>
        </div>
      </section>

      {salvando && (
        <p className="flex items-center gap-2 text-sm text-secondary-500">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-loader-4-line animate-spin" />
          </span>
          Criando a ação entre amigos…
        </p>
      )}
    </div>
  );
}

export default forwardRef(PassoRifa);